function normalizeWpaCompetitorUrl(value, ownDomains) {
  if (
    !value ||
    value.length > 2048 ||
    /[\s\\]/u.test(value) ||
    [...value].some((character) => {
      const code = character.codePointAt(0);
      return code < 32 || code === 127;
    })
  )
    throw new Error("invalid_url");
  const authority = /^(https?:\/\/)?([^/?#]+)(?:[/?#].*)?$/i.exec(value);
  if (!authority) throw new Error("invalid_url");
  let url;
  try {
    url = new URL(authority[1] ? value : `https://${value}`);
  } catch {
    throw new Error("invalid_url");
  }
  const hostname = url.hostname.toLowerCase().replace(/\.$/, "");
  const labels = hostname.split(".");
  if (
    !["https:", "http:"].includes(url.protocol) ||
    url.username ||
    url.password ||
    url.href.length > 2048 ||
    hostname.length > 253 ||
    labels.length < 2 ||
    labels.some(
      (label) => !/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(label),
    ) ||
    !/^(?:[a-z]{2,63}|xn--[a-z0-9-]{2,59})$/i.test(labels.at(-1) || "") ||
    ownDomains.some((domain) => {
      const own = domain.toLowerCase().replace(/\.+$/, "");
      return hostname === own || hostname.endsWith(`.${own}`);
    })
  )
    throw new Error("invalid_url");
  return url.href;
}

const wpaMatchedDateRequests = new Map();
function getWpaMatchedDate(productId) {
  if (!wpaMatchedDateRequests.has(productId)) {
    const promise = fetch(
      "/apps/prisstatus/prismatch-date?productId=" +
        encodeURIComponent(productId),
      { credentials: "same-origin", headers: { Accept: "application/json" } },
    )
      .then((response) => (response.ok ? response.json() : null))
      .then((result) => (result?.ok ? result.updatedAt : null))
      .catch(() => null);
    wpaMatchedDateRequests.set(productId, promise);
  }
  return wpaMatchedDateRequests.get(productId);
}
class WpaPrismatch extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const options = { signal: this.abortController.signal };
    this.dialog = this.querySelector("[data-wpa-dialog]");
    this.form = this.querySelector("[data-wpa-match-form]");
    this.tooltip = this.querySelector("[data-wpa-tooltip]");
    this.tooltipButton = this.querySelector("[data-wpa-tooltip-toggle]");
    this.messages = JSON.parse(
      this.querySelector("[data-wpa-messages]")?.textContent || "{}",
    );
    if (this.tooltip) {
      if (!this.setMatchedDate(this.tooltip.dataset.matchedAt)) {
        this.tooltipButton.hidden = true;
        getWpaMatchedDate(this.dataset.productId).then((value) => {
          if (this.isConnected && value) this.setMatchedDate(value);
        });
      }
      this.tooltipButton.addEventListener(
        "click",
        (event) =>
          this.setTooltip(
            event.pointerType === "mouse" ? true : this.tooltip.hidden,
          ),
        options,
      );
      this.tooltipButton.addEventListener(
        "pointerenter",
        (e) => {
          if (e.pointerType === "mouse") this.setTooltip(true);
        },
        options,
      );
      this.tooltipButton.addEventListener(
        "pointerleave",
        (e) => {
          if (
            e.pointerType === "mouse" &&
            document.activeElement !== this.tooltipButton
          )
            this.setTooltip(false);
        },
        options,
      );
      this.tooltipButton.addEventListener(
        "focus",
        () => {
          if (this.tooltipButton.matches(":focus-visible"))
            this.setTooltip(true);
        },
        options,
      );
      this.tooltipButton.addEventListener(
        "blur",
        () => this.setTooltip(false),
        options,
      );
      window.addEventListener(
        "resize",
        () => {
          if (!this.tooltip.hidden) this.positionTooltip();
        },
        options,
      );
    }
    this.querySelectorAll("[data-wpa-close]").forEach((button) =>
      button.addEventListener("click", () => this.dialog.close(), options),
    );
    this.dialog?.addEventListener(
      "click",
      (e) => {
        if (e.target !== this.dialog) return;
        const r = this.dialog.getBoundingClientRect();
        if (
          e.clientX < r.left ||
          e.clientX > r.right ||
          e.clientY < r.top ||
          e.clientY > r.bottom
        )
          this.dialog.close();
      },
      options,
    );
    this.dialog?.addEventListener(
      "close",
      () => {
        this.opener?.focus({ preventScroll: true });
      },
      options,
    );
    this.dialog?.addEventListener(
      "keydown",
      (e) => {
        if (e.key !== "Tab") return;
        const items = [
          ...this.dialog.querySelectorAll(
            'button:not([disabled]),input:not([disabled]):not([tabindex="-1"]),textarea:not([disabled]),select:not([disabled]),a[href]',
          ),
        ].filter((el) => el.getClientRects().length);
        const first = items[0];
        const last = items.at(-1);
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      },
      options,
    );
    this.form?.addEventListener("submit", (e) => this.submit(e), options);
    this.form?.addEventListener("input", () => this.clearFeedback(), options);
  }
  disconnectedCallback() {
    this.abortController?.abort();
  }
  setMatchedDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;
    const formatted = new Intl.DateTimeFormat(this.dataset.locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "Europe/Copenhagen",
    }).format(date);
    this.tooltip.textContent = this.tooltip.dataset.dateTemplate.replace(
      "DATE",
      formatted,
    );
    this.tooltip.dataset.matchedAt = value;
    this.tooltipButton.hidden = false;
    return true;
  }
  setTooltip(open) {
    if (!this.tooltip) return;
    this.tooltip.hidden = !open;
    this.tooltipButton.setAttribute("aria-expanded", String(open));
    if (open) this.positionTooltip();
  }
  positionTooltip() {
    this.tooltip.style.insetInlineStart = "0px";
    const bounds = this.tooltip.getBoundingClientRect();
    const margin = 16;
    const viewportWidth = document.documentElement.clientWidth;
    const offset =
      bounds.right > viewportWidth - margin
        ? viewportWidth - margin - bounds.right
        : bounds.left < margin
          ? margin - bounds.left
          : 0;
    this.tooltip.style.insetInlineStart = `${offset}px`;
  }
  open(opener) {
    if (!this.dialog) return;
    this.opener = opener;
    this.setTooltip(false);
    this.clearFeedback();
    this.dialog.showModal();
    this.dialog.scrollTop = 0;
    const initialFocus =
      this.dialog.querySelector("[data-wpa-dialog-heading]") ||
      this.dialog.querySelector("[data-wpa-close]");
    initialFocus?.focus({ preventScroll: true });
    void this.loadGuaranteeFactor();
  }
  async loadGuaranteeFactor() {
    const choice = this.querySelector("[data-wpa-new-comparison]");
    const key = [
      this.dataset.productId,
      this.dataset.variantId,
      this.dataset.country,
    ].join(":");
    if (
      !choice ||
      this.factorRequest ||
      (this.factorLoadedKey === key &&
        Date.now() - (this.factorLoadedAt || 0) < 60000)
    )
      return this.factorRequest;
    if (this.factorLoadedKey && this.factorLoadedKey !== key)
      this.setGuaranteeFactor(null);
    const controller = new AbortController();
    const cancel = () => controller.abort();
    this.abortController.signal.addEventListener("abort", cancel, {
      once: true,
    });
    const timeout = setTimeout(cancel, 5000);
    const fieldset = choice.closest("fieldset");
    fieldset?.setAttribute("aria-busy", "true");
    this.querySelector("[data-wpa-unsupported-grade]").hidden = true;
    const current = () =>
      this.isConnected &&
      key ===
        [
          this.dataset.productId,
          this.dataset.variantId,
          this.dataset.country,
        ].join(":");
    this.factorRequest = (async () => {
      try {
        const query = new URLSearchParams({
          productId: this.dataset.productId,
          variantId: this.dataset.variantId,
          country: this.dataset.country,
        });
        const response = await fetch(
          `/apps/prisstatus/prismatch-factor?${query}`,
          {
            credentials: "same-origin",
            headers: { Accept: "application/json" },
            cache: "no-store",
            signal: controller.signal,
          },
        );
        const result = await response.json();
        if (
          !response.ok ||
          !result.ok ||
          result.productId !==
            `gid://shopify/Product/${this.dataset.productId}` ||
          result.variantId !==
            `gid://shopify/ProductVariant/${this.dataset.variantId}` ||
          !(
            result.factorBps === null ||
            (Number.isInteger(result.factorBps) &&
              result.factorBps > 0 &&
              result.factorBps <= 10000)
          )
        )
          throw new Error("unavailable");
        if (!current()) return;
        this.setGuaranteeFactor(result.factorBps);
        this.factorLoadedAt = Date.now();
        this.factorLoadedKey = key;
      } catch {
        if (current()) {
          this.setGuaranteeFactor(null);
          this.querySelector("[data-wpa-unsupported-grade]").hidden = true;
          this.feedback("unavailable");
        }
      } finally {
        clearTimeout(timeout);
        this.abortController.signal.removeEventListener("abort", cancel);
        fieldset?.removeAttribute("aria-busy");
        this.factorRequest = undefined;
      }
    })();
    return this.factorRequest;
  }
  setGuaranteeFactor(factorBps) {
    const enabled = factorBps !== null;
    this.dataset.guaranteeFactor = enabled ? String(factorBps) : "";
    this.dataset.newComparisonEnabled = String(enabled);
    this.querySelector("[data-wpa-new-comparison]").hidden = !enabled;
    this.querySelector("[data-wpa-unsupported-grade]").hidden = enabled;
    const input = this.form.querySelector(
      '[name="comparisonType"][value="new"]',
    );
    input.disabled = !enabled;
    if (!enabled && input.checked) {
      input.checked = false;
      this.form.querySelector('[name="comparisonType"][value="used"]').checked =
        true;
    }
  }
  async submit(event) {
    event.preventDefault();
    if (this.busy) return;
    const data = new FormData(this.form);
    let url;
    try {
      url = normalizeWpaCompetitorUrl(
        String(data.get("url") || ""),
        [
          location.hostname,
          this.dataset.shopDomain,
          "weplayagain.com",
          "weplay-again.myshopify.com",
        ].filter(Boolean),
      );
    } catch {
      this.feedback("invalid_url");
      this.form.querySelector('[name="url"]')?.focus();
      return;
    }
    if (!this.form.reportValidity()) return;
    const comparisonType = String(data.get("comparisonType") || "");
    const competitorPrice = String(data.get("competitorPrice") || "").trim();
    const comment = String(data.get("comment") || "");
    if (!["used", "new"].includes(comparisonType)) {
      this.feedback("validation");
      return;
    }
    if (
      comparisonType === "new" &&
      this.dataset.newComparisonEnabled !== "true"
    ) {
      this.feedback("unsupported_grade");
      return;
    }
    const competitorAmount = Number(competitorPrice.replace(",", "."));
    if (
      !/^\d+(?:[.,]\d{1,2})?$/.test(competitorPrice) ||
      !Number.isFinite(competitorAmount) ||
      competitorAmount <= 0 ||
      competitorAmount > 1000000 ||
      comment.length > 2000
    ) {
      this.feedback("validation");
      return;
    }
    const displayedGrade = this.querySelector(
      "[data-wpa-display-grade]",
    )?.textContent.trim();
    const displayedVariant = this.querySelector(
      "[data-wpa-display-variant]",
    )?.textContent.trim();
    const displayedComparison = this.querySelector(
      `[data-wpa-comparison-label="${comparisonType}"]`,
    )?.textContent.trim();
    const presentation =
      displayedGrade && displayedVariant && displayedComparison
        ? {
            grade: displayedGrade,
            variant: displayedVariant,
            comparisonType: displayedComparison,
          }
        : undefined;
    this.busy = true;
    const button = this.querySelector("[data-wpa-send]");
    button.disabled = true;
    button.setAttribute("aria-busy", "true");
    button.textContent = this.messages.sending;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch("/apps/prisstatus/prismatch", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        signal: controller.signal,
        body: JSON.stringify({
          productId: this.dataset.productId,
          variantId: this.dataset.variantId,
          productTitle: this.dataset.productTitle,
          variantTitle: this.dataset.variantTitle,
          productUrl: this.dataset.productUrl,
          marketId: this.dataset.marketId || undefined,
          presentation,
          grade: this.dataset.grade || "",
          comparisonType,
          expectedFactorBps:
            comparisonType === "new"
              ? Number(this.dataset.guaranteeFactor)
              : undefined,
          competitorPrice,
          comment,
          price: Number(this.dataset.price),
          currency: this.dataset.currency,
          country: this.dataset.country,
          email: String(data.get("email")).trim(),
          url,
          locale: this.dataset.locale,
          honeypot: String(data.get("honeypot") || ""),
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.ok) {
        this.feedback(result.error || "unavailable");
        return;
      }
      this.feedback("success");
      this.form.reset();
    } catch {
      this.feedback("unavailable");
    } finally {
      clearTimeout(timeout);
      this.busy = false;
      button.disabled = false;
      button.removeAttribute("aria-busy");
      button.textContent = this.messages.send;
    }
  }
  clearFeedback() {
    const output = this.querySelector("[data-wpa-feedback]");
    if (!output) return;
    output.textContent = "";
    output.hidden = true;
    output.removeAttribute("data-success");
  }
  feedback(key) {
    const output = this.querySelector("[data-wpa-feedback]");
    output.textContent = this.messages[key] || this.messages.unavailable;
    output.hidden = false;
    output.toggleAttribute("data-success", key === "success");
  }
}
if (!customElements.get("wpa-prismatch"))
  customElements.define("wpa-prismatch", WpaPrismatch);
document.addEventListener("click", (event) => {
  const opener = event.target.closest("[data-wpa-open]");
  if (opener) {
    const root = [...document.querySelectorAll("wpa-prismatch")].find(
      (el) => el.dataset.formId === opener.dataset.wpaOpen,
    );
    root?.open(opener);
  }
  document.querySelectorAll("wpa-prismatch").forEach((root) => {
    if (!root.contains(event.target)) root.setTooltip(false);
  });
});
document.addEventListener("keydown", (event) => {
  if (event.key === "Escape")
    document
      .querySelectorAll("wpa-prismatch")
      .forEach((root) => root.setTooltip(false));
});
function expireRenderedPriceLabels() {
  const now = Date.now();
  document.querySelectorAll("[data-wpa-valid-to]").forEach((element) => {
    const expiresAt = Date.parse(element.dataset.wpaValidTo);
    if (!Number.isFinite(expiresAt) || expiresAt <= now) element.remove();
  });
  document
    .querySelectorAll("[data-wpa-campaign-current]")
    .forEach((element) => {
      const expiresAt = Date.parse(element.dataset.wpaCampaignCurrent);
      if (!Number.isFinite(expiresAt) || expiresAt <= now) {
        element.classList.remove("text-on-sale");
        if (element.dataset.wpaRegularClasses)
          element.className = element.dataset.wpaRegularClasses;
        const label = element.querySelector(".sr-only");
        if (label && element.dataset.wpaCurrentLabel)
          label.textContent = element.dataset.wpaCurrentLabel;
        delete element.dataset.wpaCampaignCurrent;
      }
    });
}
expireRenderedPriceLabels();
setInterval(expireRenderedPriceLabels, 15000);
document.addEventListener(
  "change",
  (event) => {
    const target = event.target;
    const card = target.closest("product-card,.reco-card");
    if (!card) return;
    const variantId =
      target.getAttribute("data-variant-id") ||
      (target.matches(".reco-card__variant-select") ? target.value : null);
    if (!variantId || !/^\d+$/.test(variantId)) return;
    const template = card.querySelector(
      `template[data-wpa-price-variant="${variantId}"]`,
    );
    const price = card.querySelector(
      ".product-card__info price-list,.reco-card__price price-list",
    );
    if (!template || !price) return;
    const replacement = template.content.firstElementChild.cloneNode(true);
    price.replaceWith(replacement);
    const badgeTemplate = card.querySelector(
      `template[data-wpa-badges-variant="${variantId}"]`,
    );
    const badges = card.querySelector("[data-wpa-variant-badges]");
    if (badgeTemplate && badges)
      badges.replaceChildren(badgeTemplate.content.cloneNode(true));
    const recommendationBadgeTemplate = card.querySelector(
      `template[data-wpa-reco-badge-variant="${variantId}"]`,
    );
    const recommendationBadge = card.querySelector("[data-wpa-reco-badge]");
    if (recommendationBadgeTemplate && recommendationBadge)
      recommendationBadge.replaceChildren(
        recommendationBadgeTemplate.content.cloneNode(true),
      );
    if (card.classList.contains("reco-card")) {
      card
        .querySelectorAll(".reco-card__title,.reco-card__media")
        .forEach((link) => {
          const url = new URL(link.href);
          url.searchParams.set("variant", variantId);
          link.href = url.href;
        });
    }
    const unlock = card.querySelector(".reco-card__unlock");
    if (unlock)
      unlock.hidden = Boolean(
        replacement.querySelector(
          "[data-wpa-price-status],compare-at-price,.reco-card__price-was",
        ),
      );
    expireRenderedPriceLabels();
  },
  true,
);
